// We don't have apollo-cache as a direct dependency, but we're pretty sure it'll stay there.
// We'll avoid having it as a direct dependency (with explicit versioning et al) since we use it just here once.
// tslint:disable-next-line:no-implicit-dependencies
import { DataProxy } from 'apollo-cache';
import { MutationUpdaterFn } from 'apollo-client';
import { DocumentNode } from 'graphql';

interface SimpleQueryUpdaterOptions<TData, TQuery, TVariables = {}> {
    query: DocumentNode;
    variables?: TVariables | ((data: TData) => TVariables);

    update(data: TData, queryData: TQuery): void | TQuery;
}

export function apolloSimpleQueryUpdaterFactory<TData, TQuery, TVariables = {}>(
    options: SimpleQueryUpdaterOptions<TData, TQuery, TVariables>): MutationUpdaterFn<TData> {

    return (cache, { data }) => {
        if (!data) return;

        const variables = typeof options.variables == 'function'
            ? (options.variables as (data: TData) => TVariables)(data)
            : options.variables;

        const { read, write } = apolloReadWriteQuery<TQuery>(cache, options.query, variables);

        let queryData;
        try {
            queryData = read();
            if (!queryData) return;
        } catch {
            return;
        }

        const newQueryData = options.update(data, queryData);
        if (!newQueryData) return;

        write(newQueryData);
    };
}

export function apolloReadWriteQuery<TQuery, TVariables = {}>(
    cache: DataProxy,
    query: DocumentNode,
    variables?: TVariables) {

    const queryRef: DataProxy.Query<TVariables> = { query, variables };

    return { read, write };

    function read(): TQuery | null {
        return cache.readQuery(queryRef);
    }

    function write(data: TQuery): void {
        return cache.writeQuery({ ...queryRef, data });
    }
}
